import { ItemStatusSliceType } from "../../../services/redux/slices/itemStatusSlice";
import {
  CartItemType,
  CartLoyaltyV2SliceType,
  ItemOfferDetails,
  ItemOfferPayload,
  OfferPayload,
} from "../../../services/redux/types/CartLoyaltyV2Types";
import { replaceNullWithUndefined } from "../../../services/redux/types/TypeHelper";
import CartItem from "../../CartItem";
import { MAIN_OFFER_TYPE, OrderTypesEnum } from "../../enums";
import {
  getAllUnavailableItemIds,
  isItemHaveAddons,
} from "../../help/itemDetails/itemDetails";
import { DoubleLinkedList } from "../../help/linkedList/LinkedList";
import { nextBogoStep } from "../../help/offers/offers";
import {
  AddonGroupType,
  DiscountedItemOfferDetailsType,
  ItemOfferType,
  ItemType,
  OfferType,
  SubItemType,
} from "../../types";
import Offer from "../Offer/Offer";
import { IsOfferApplicableArgs } from "../types";
const lodashClonedeep = require("lodash.clonedeep");

export class ItemOffer extends Offer {
  offerType: MAIN_OFFER_TYPE | string;
  details: ItemOfferDetails;

  constructor(offer: ItemOfferType) {
    super(offer as OfferType);
    this.offerType = offer.offerType;
    this.details = offer.details;
    this.isCartV2 = true;
  }

  numberOfSteps() {
    if (this.isBundleOffer()) {
      return this.details.subItems.length;
    }

    let totalSteps = 1; // Item Customization
    if (this.details.subItems.length > 1) {
      totalSteps += 1; // Choose Item
    }

    return totalSteps;
  }

  applyBundleOffer(
    cartV2: CartLoyaltyV2SliceType,
    cartItem?: CartItem
  ): OfferPayload | null {
    if (
      cartV2.selectedOfferSubItems.size === 0 ||
      Array.isArray(cartV2.selectedOfferSubItems)
    ) {
      cartV2.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
      this.details.subItems
        .sort((a, b) => (b?.price ?? 0) - (a?.price ?? 0))
        .forEach((subItem) => {
          const itemData = this.itemWithoutAddons(subItem);
          let item = itemData.toJson();
          item = replaceNullWithUndefined(item) as CartItemType;
          cartV2.selectedOfferSubItems.append(item);
        });
    }

    if (cartItem) {
      let itemData = cartItem.toJson();
      itemData = replaceNullWithUndefined(itemData) as CartItemType;

      cartV2.selectedOfferSubItems.updateHead(itemData);
      // const nextItem = cartV2.selectedOfferSubItems.moveForward();

      if (cartV2.selectedOfferSubItems.hasNext()) {
        // cartV2.redirectToItem = {
        //   itemId: nextItem.subItemDetails.itemId!,
        //   subItemId: nextItem.subItemDetails.id!,
        // };
        cartV2.selectedOfferId = this.id;

        return null;
      }
    } else {
      const head = cartV2.selectedOfferSubItems.getHead();
      if (head) {
        cartV2.redirectToItem = {
          itemId: head.subItemDetails.itemId!,
          subItemId: head.subItemDetails.id!,
        };
        cartV2.selectedOfferId = this.id;

        return null;
      }
    }

    const bundleItems = cartV2.selectedOfferSubItems.toArray();
    cartV2.redirectToItem = undefined;
    cartV2.selectedOfferId = -1;
    cartV2.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();

    return {
      id: this.id,
      offerType: this.offerType,
      offerCategory: this.offerCategory,
      details: this.details,
      uiDetails: this.uiDetails,
      items: bundleItems,
    };
  }

  applyOffer(
    cartV2: CartLoyaltyV2SliceType,
    cartItem?: CartItem[]
  ): OfferPayload | null {
    if (
      this.details.subItems.length > 1 &&
      this.offerType === MAIN_OFFER_TYPE.BUNDLE
    ) {
      return this.applyBundleOffer(cartV2, cartItem ? cartItem[0] : undefined);
    }

    let needRedirect = false;

    if (!cartItem) {
      const subItems = this.details.subItems;

      needRedirect = subItems?.some((subItem) =>
        isItemHaveAddons(subItem.allAddonGroups ?? [])
      );

      // Always open item screen if the offer is sub offer
      needRedirect = needRedirect || (this.isSubOffer ?? false);
    }

    if (needRedirect) {
      const subItem =
        this.details.subItems.find((subItem) =>
          cartItem?.some((item) => item.subItemDetails.id === subItem.id)
        ) ?? this.details.subItems[0];
      if (this.isSubOffer && cartV2.selectedOfferSubItems.size === 0) {
        cartV2.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
        this.details.subItems.forEach((subItem) => {
          const itemData = this.itemWithoutAddons(subItem);
          let item = itemData.toJson();
          item = replaceNullWithUndefined(item) as CartItemType;
          cartV2.selectedOfferSubItems.append(item);
        });
      }

      if (this.isSubOffer) {
        cartV2.bogoSteps = nextBogoStep(cartV2.bogoSteps); // Increment BOGO step
      }
      cartV2.redirectToItem = {
        itemId: subItem.itemId!,
        subItemId: subItem.id!,
      };
      cartV2.selectedOfferId = this.id;

      return null;
    }

    let itemData;
    let itemRedeemed = {} as CartItemType;

    if (!cartItem) {
      itemData = this.itemWithoutAddons(this.details.subItems[0]);
    } else {
      itemData = cartItem[0];
    }

    if (itemData) {
      itemRedeemed = itemData.toJson();
      itemRedeemed = replaceNullWithUndefined(itemRedeemed) as CartItemType;
    }

    cartV2.redirectToItem = undefined;
    cartV2.selectedOfferId = -1;
    ItemOffer.clearRedirectItem(cartV2);

    return {
      id: this.id,
      offerType: this.offerType,
      offerCategory: this.offerCategory,
      item: itemRedeemed,
      details: this.details,
      uiDetails: this.uiDetails,
    };
  }

  itemWithoutAddons(subItem: SubItemType): CartItem {
    const discountPercentage = this.isDiscountItemOffer()
      ? (this.details as DiscountedItemOfferDetailsType).value
      : undefined;

    const itemData = new CartItem(
      "",
      this.details.count || 1,
      0,
      {
        ...subItem,
        customFranchiseSubItemPrices:
          subItem.customFranchiseSubItemPrices ?? [],
      },
      { ...subItem.item!, subItems: [] },
      undefined,
      -1,
      undefined,
      discountPercentage,
      (subItem.item as ItemType).customDeliveryTime,
      (subItem.item as ItemType).excludeFromOffers,
      false,
      this.offerType === MAIN_OFFER_TYPE.BUNDLE
    );

    itemData.subItemDetails.item = {
      ...itemData.subItemDetails.item!,
      subItems: [],
    };
    const itemWithEmptyAddons = lodashClonedeep(itemData.subItemDetails);
    itemWithEmptyAddons.allAddonGroups?.forEach(
      (addonGroup: AddonGroupType) => {
        addonGroup.addonGroupSubItem = [];
      }
    );
    itemWithEmptyAddons.basePrice = itemWithEmptyAddons.price;
    itemWithEmptyAddons.previousPrice = -1;

    itemData.subItemDetails = itemWithEmptyAddons;

    return itemData;
  }

  static clearRedirectItem(cartV2: CartLoyaltyV2SliceType) {
    cartV2.redirectToItem = undefined;
    cartV2.selectedOfferId = -1;
    cartV2.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
  }

  protected compareOffer(offer: Offer) {
    if (!(offer instanceof ItemOffer) || this.id !== offer.id) {
      return false;
    }
    const itemOffer = offer as ItemOffer;

    if (this.details.subItems.length !== itemOffer.details.subItems.length) {
      return false;
    }

    const thisSubItemIds = this.details.subItems
      .map((subItem) => subItem.item?.id)
      .sort();
    const offerSubItemIds = itemOffer.details.subItems
      .map((subItem) => subItem.item?.id)
      .sort();

    if (!thisSubItemIds?.every((id, index) => id === offerSubItemIds[index])) {
      return false;
    }

    return true;
  }

  protected compareRedeemedOffer(offer: OfferPayload) {
    if (!(offer as ItemOfferPayload) || this.id !== offer.id) {
      return false;
    }
    const itemOffer = offer as ItemOfferPayload;

    return (
      this.offerType === MAIN_OFFER_TYPE.BUNDLE ||
      this.details.subItems
        .map((s) => s.id)
        .includes(itemOffer?.item?.subItemDetails?.id)
    );
  }

  priceWithoutDiscount(): number {
    return this.details.subItems.reduce(
      (total, subItem) => total + subItem.price,
      0
    );
  }

  isItemOffer() {
    return true;
  }

  isItemFromListOffer() {
    return (
      this.details.subItems.length > 1 &&
      this.offerType !== MAIN_OFFER_TYPE.BUNDLE
    );
  }

  isFreeItemOffer(): boolean {
    return this.offerType === MAIN_OFFER_TYPE.FREE_ITEM;
  }

  isDiscountItemOffer(): boolean {
    return this.offerType === MAIN_OFFER_TYPE.DISCOUNT_ITEM;
  }

  isBundleOffer() {
    return this.offerType === MAIN_OFFER_TYPE.BUNDLE;
  }

  isOfferApplicableByType({
    itemStatusRedux,
    orderType,
  }: IsOfferApplicableArgs): boolean {
    const { allUnavailableItemIds } = getAllUnavailableItemIds(
      itemStatusRedux,
      orderType
    );

    const offerHasUnavailableItems = this.details.subItems.some((subItem) =>
      allUnavailableItemIds.includes(subItem.item?.id!)
    );
    const allItemsAreUnavailable = this.details.subItems.every((subItem) =>
      allUnavailableItemIds.includes(subItem.item?.id!)
    );

    if (this.isBundleOffer() && offerHasUnavailableItems) {
      return false;
    }

    if (allItemsAreUnavailable) {
      return false;
    }

    return true;
  }

  hasDeletedItems(): boolean {
    if (this.isBundleOffer()) {
      return (
        this.details.subItems.some(
          (subItem) => subItem.deletedAt || subItem.item?.deletedAt
        ) || false
      );
    }

    return (
      this.details.subItems.every(
        (subItem) => subItem.deletedAt || subItem.item?.deletedAt
      ) || false
    );
  }

  isOfferOutOfStock({
    itemStatusRedux,
    orderType,
  }: {
    itemStatusRedux: ItemStatusSliceType;
    orderType?: OrderTypesEnum;
  }): boolean {
    const { outOfStockItemIds } = getAllUnavailableItemIds(
      itemStatusRedux,
      orderType
    );

    const offerHasOutOfStockItems = this.details.subItems.some((subItem) =>
      outOfStockItemIds.includes(subItem.item?.id!)
    );
    const allItemsAreOutOfStock = this.details.subItems.every((subItem) =>
      outOfStockItemIds.includes(subItem.item?.id!)
    );

    if (this.isBundleOffer() && offerHasOutOfStockItems) {
      return true;
    }

    if (allItemsAreOutOfStock) {
      return true;
    }

    return false;
  }

  shouldHideOffer(disabledItemsIds: number[]) {
    if (this.isBundleOffer()) {
      return (
        this.details.subItems.some(
          (subItem) =>
            subItem.itemId && disabledItemsIds.includes(subItem.itemId)
        ) ?? false
      );
    }

    return (
      this.details.subItems.every(
        (subItem) => subItem.itemId && disabledItemsIds.includes(subItem.itemId)
      ) || false
    );
  }

  getWelcomePopupOfferText(): string {
    return this.isBundleOffer()
      ? Offer.getLocalizedValue(this.uiDetails?.buttonText) ?? ""
      : Offer.getLocalizedValue(this.uiDetails?.mainText) ?? "";
  }
}
